import { type UIEvent, useEffect, useState, type FC, Fragment } from 'react'
import { observer } from 'mobx-react-lite'
import { Scrollbar, SpinnerLoader, Typography } from 'shared/ui'
import { UserDropdownCard } from 'entities/Users'
import { type IUser } from 'entities/Users/model/types'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type SearchDropdownUsersStore } from 'widgets/SearchDropdown/store/searchDropdownUsersStore'
import { getLimitReachedTooltip } from 'widgets/SearchDropdown/constants/limitReachedTooltip'
import styles from './styles.module.scss'

type IUserDropdownContentProps = {
  store: SearchDropdownUsersStore
  onChange?: (item: IUser) => void
}

export const UsersDropdownContentItems: FC<IUserDropdownContentProps> = observer(({ store }) => {
  const { usersStore } = useEntitiesContext()
  const {
    options,
    itemsListIds,
    search,
    loading,
    hasMore,
    loadMore,
    hasItems,
    init,
    maxLimit,
    selectedItemsCount,
  } = store
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const isMaxLimitReached = maxLimit !== null && selectedItemsCount >= maxLimit

  const handleScroll = (e: UIEvent) => {
    const target = e.target as HTMLDivElement
    const scrollHeight = target.scrollHeight
    const scrollTop = target.scrollTop
    const offsetHeight = target.offsetHeight

    if (loading) return
    if (!hasMore) return
    if (scrollHeight <= scrollTop + offsetHeight + 100) {
      loadMore()
    }
  }

  const handleSelect = (item: IUser) => {
    store.selectTypeItem(item)
  }

  const isSelected = (id: number | undefined) => {
    if (id === undefined) return false

    return store.isSelected(id)
  }

  useEffect(() => {
    setSelectedIds(store.selectedItemsListIds)

    if (!loading) {
      init()
    }
  }, [])

  const unassignedItem = store.getUnassignedItem(search)
  const actualItemsListIds = selectedIds.includes(0)
    ? itemsListIds
    : unassignedItem
    ? [0, ...itemsListIds]
    : itemsListIds

  const getUnassignedItem = () => {
    return unassignedItem && !selectedIds.includes(0) ? unassignedItem : undefined
  }

  return (
    <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={290} onScroll={handleScroll}>
      <div className={styles.list}>
        {[...selectedIds, ...actualItemsListIds].map((id, index) => {
          const isUnassignedItem = id === 0
          const item = isUnassignedItem
            ? getUnassignedItem()
            : (usersStore.getItem(id) as IUser | undefined)

          if (!item) return

          const isDisabled = isMaxLimitReached && !isSelected(id)
          const tooltipProps = isMaxLimitReached ? getLimitReachedTooltip(true) : undefined
          const inputCheckboxProps = options?.selectedMode
            ? {
                checked: isSelected(id),
                onChecked: () => handleSelect(item),
              }
            : undefined

          return (
            <Fragment key={item.id}>
              <UserDropdownCard
                item={item}
                withoutOnlineStatus={isUnassignedItem}
                InputCheckboxProps={inputCheckboxProps}
                onChange={handleSelect}
                tooltipProps={tooltipProps}
                disabled={isDisabled}
              />
              {index === selectedIds.length - 1 && <div className={styles.divider}></div>}
            </Fragment>
          )
        })}
        {!hasItems && !unassignedItem && !loading && (
          <div className={styles.emptyList}>
            <Typography variant={'body-rg-regular'} ariaLabel={'NotFound'}>
              Not found
            </Typography>
          </div>
        )}
        {loading && (
          <div className={styles.spinner}>
            <SpinnerLoader ariaLabel={'UsersDropdownContentItems'} />
          </div>
        )}
      </div>
    </Scrollbar>
  )
})
